import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BrandContext } from "../../App";
import EngagementChart from "../simple/EngagementChart";
import EngagementFilter from "../simple/EngagementFilter";
import EngagementAchievementBox from "../simple/EngagementAchievementBox";
import { NightlightOutlined, StackedLineChart } from "@mui/icons-material";

import DefaultButton from "../simple/DefaultButton";
import { useAPI } from "../../hooks/useAPI";

import { useAuth } from "../../contexts/AuthContext";

const TeamEngagementPanel = () => {
  const { brand } = React.useContext(BrandContext);

  const { loggedInUser, authToken } = useAuth();

  const theme = useTheme();
  const { callAPI } = useAPI();
  const teamId = loggedInUser?.teams?.length
    ? loggedInUser?.teams[0]?.id
    : null;
  const minCustomInput = 1;
  const maxCustomInput = 120;

  const [dataPoints, setDataPoints] = useState([]);
  const [filter, setFilter] = useState("all-members");
  const [teamUsers, setTeamUsers] = useState([]);
  const [isManager, setIsManager] = useState(false);
  const [days, setDays] = useState(7);
  const [mostActive, setMostActive] = useState(null);
  const [leastActive, setLeastActive] = useState(null);
  const [mostActiveUser, setMostActiveUser] = useState(null);
  const [leastActiveUser, setLeastActiveUser] = useState(null);
  const [customInput, setCustomInput] = useState("");
  const [customInputError, setCustomInputError] = useState(false);

  const handleChange = (event) => {
    setFilter(event.target.value);
  };

  const handleViewChange = (_, newValue) => {
    if (newValue !== null) {
      setDays(newValue);
    }
  };

  const cache = useRef({});

  useEffect(() => {
    if (customInput.length === 0) return;
    const timeOutId = setTimeout(() => setDays(customInput), 500);
    return () => clearTimeout(timeOutId);
  }, [customInput]);

  useEffect(() => {
    if (!teamId) return;

    const cacheKey = `${teamId}_${filter}_${days}`;

    // Helper function to process data (so we can use it for both Cache and Fetch)
    const processData = (points) => {
      // 1. Existing Logic: Find highest/lowest single DAY points
      let highestDay = points[0];
      let lowestDay = points[0];

      // 2. NEW Logic: Aggregate totals by User
      const userTotals = points.reduce((acc, curr) => {
        // Skip invalid users or team-aggregated rows if any
        if (!curr.userId || curr.userId === "-") return acc;

        if (!acc[curr.userId]) {
          acc[curr.userId] = { userId: curr.userId, totalSims: 0 };
        }
        acc[curr.userId].totalSims += curr.numberOfSims;
        return acc;
      }, {});

      const userList = Object.values(userTotals);

      // Sort to find max and min users
      userList.sort((a, b) => b.totalSims - a.totalSims); // Descending

      const topUser = userList.length > 0 ? userList[0] : null;
      const bottomUser =
        userList.length > 0 ? userList[userList.length - 1] : null;

      // Calculate Day highs/lows (your original logic)
      for (const point of points) {
        if (point.numberOfSims > highestDay.numberOfSims) highestDay = point;
        if (point.numberOfSims < lowestDay.numberOfSims) lowestDay = point;
      }

      return { highestDay, lowestDay, topUser, bottomUser };
    };

    // --- CACHE HANDLING ---
    if (cache.current[cacheKey]) {
      const cached = cache.current[cacheKey];
      setDataPoints(cached.points);
      setMostActive(cached.most);
      setLeastActive(cached.least);
      // Set new user states from cache
      setMostActiveUser(cached.topUser);
      setLeastActiveUser(cached.bottomUser);
      return;
    }

    // --- FETCH HANDLING ---
    const params = new URLSearchParams();
    params.set("days", days);

    if (filter === "all-members") {
      params.set("team_id", teamId);
    } else {
      params.set("team_id", teamId);
      params.set("user_id", filter);
    }

    callAPI(`leaderboard_data?${params.toString()}`, {
      token: authToken,
    })
      .then((data) => {
        const sorted = [...data.data].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

        const points = sorted.map((entry) => ({
          userId: entry.user_id,
          date: entry.date,
          numberOfSims: entry.number_of_sims,
          averageScore: entry.average_total_score,
        }));

        // Run the processing logic
        const { highestDay, lowestDay, topUser, bottomUser } =
          processData(points);

        cache.current[cacheKey] = {
          points,
          most: highestDay,
          least: lowestDay,
          topUser: topUser,
          bottomUser: bottomUser,
        };

        setDataPoints(points);
        setMostActive(highestDay);
        setLeastActive(lowestDay);
        setMostActiveUser(topUser);
        setLeastActiveUser(bottomUser);
      })
      .catch((err) => console.error(err.message));
  }, [teamId, filter, days]);

  useEffect(() => {
    if (!teamId || dataPoints.length === 0 || dataPoints[0].userId === "-")
      return;

    callAPI(`team_users?team_id=${teamId}`, { token: authToken })
      .then((data) => {
        setTeamUsers(data);
        setIsManager(true);
      })
      .catch((err) => {
        console.error(err);
        throw new Error(err);
      });
  }, [teamId, dataPoints]);

  const getUserName = (id) => {
    const found = teamUsers.find((u) => u.id === id);
    return found ? `${found.first_name} ${found.last_name}` : "Unknown User";
  };

  const handleExport = () => {
    // 1. Check if there is data to export
    if (!dataPoints || dataPoints.length === 0) {
      console.warn("No data to export");
      return;
    }

    // 2. Define CSV Headers
    const headers = ["Date", "User Name", "User ID", "Number of Sims"];

    // 3. Convert dataPoints to CSV rows
    const csvRows = dataPoints.map((point) => {
      // Get the readable name using your existing helper
      const rawName = getUserName(point.userId);
      // Escape quotes in names just in case to prevent CSV breakage
      const safeName = rawName ? `"${rawName.replace(/"/g, '""')}"` : '""';

      return [point.date, safeName, point.userId, point.numberOfSims].join(",");
    });

    // 4. Combine headers and rows with newlines
    const csvContent = [headers.join(","), ...csvRows].join("\n");

    // 5. Create a Blob and trigger the download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute(
      "download",
      `engagement_data_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();

    // 6. Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: theme.containers.paddingMobile,
        flexGrow: 1,
        color: theme.palette.text.primary,
        backgroundColor: "transparent",
        border: `2px solid`,
        borderColor: theme.palette.border.tertiary,
        borderRadius: theme.shape.radius,
        boxShadow:
          "0px 12px 16px -4px rgba(0, 91, 140, 0.10), 0px 4px 6px -2px rgba(0, 91, 140, 0.03), 0px 2px 2px -1px rgba(0, 91, 140, 0.04)",
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: theme.typography.textMD.fontSize,
            lineHeight: theme.typography.textMD.lineHeight,
            fontWeight: theme.typography.fontWeightMedium,
            marginBottom: "12px",
          }}
        >
          Team Engagement
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            lineHeight: "1.3",
            fontWeight: theme.typography.fontWeightLight,
            fontStyle: "italic",
            mb: theme.customSpacing["2xl"],
          }}
        >
          See how actively your team is engaging with the program. Switch
          between day, week, or month views to spot patterns!
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          mb: theme.customSpacing.xl,
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            gap: "10px",
          },
        }}
      >
        <Box
          sx={{
            mr: theme.customSpacing["8xl"],
            [theme.breakpoints.down("md")]: {
              mr: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Typography
              sx={{
                fontSize: theme.typography.textXS.fontSize,
                lineHeight: theme.typography.textXS.lineHeight,
                fontWeight: theme.typography.fontWeightLight,
                fontStyle: "italic",
                mb: theme.customSpacing.sm,
              }}
            >
              View:
            </Typography>
            <Box sx={{ flexGrow: 1 }}></Box>
            <Typography
              sx={{
                fontSize: theme.typography.textXS.fontSize,
                lineHeight: theme.typography.textXS.lineHeight,
                fontWeight: theme.typography.fontWeightLight,
                fontStyle: "italic",
                textAlign: "left",
                width: 100,
                mb: theme.customSpacing.sm,
                marginRight: theme.customSpacing.lg,
              }}
            >
              Custom range:
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={days}
            exclusive
            onChange={handleViewChange}
            sx={{
              backgroundColor: "transparent",
              height: 43,
              [theme.breakpoints.down("md")]: {
                justifyContent: "space-between",
                width: "100%",
              },
              "& .MuiToggleButton-root": {
                color: theme.palette.foreground.button_secondary,
                textTransform: "none",
                border: "none",
                borderRadius: 2,
                px: theme.customSpacing.lg,
                py: theme.customSpacing.md,
                fontWeight: theme.typography.fontWeightMedium,
                "&.Mui-selected": {
                  backgroundColor: "transparent",
                  color: theme.palette.foreground.badge_icon,
                  border: "1px solid",
                  borderColor: theme.palette.border.tertiary,
                },
                "&:hover": {
                  backgroundColor: "rgba(154, 92, 255, 0.05)",
                },
              },
            }}
          >
            <div>
              <ToggleButton value={7}>Weekly</ToggleButton>
              <ToggleButton value={31}>Monthly</ToggleButton>
            </div>
            <TextField
              type="number"
              placeholder={`${minCustomInput}-${maxCustomInput} days`}
              variant="outlined"
              size="medium"
              error={customInputError}
              helperText={
                customInputError
                  ? `${minCustomInput}-${maxCustomInput} days`
                  : ""
              }
              FormHelperTextProps={{
                sx: {
                  fontSize: theme.typography.textXS.fontSize,
                  color: theme.palette.text.error_primary,
                  overflow: "",
                },
              }}
              onInput={(e) => {
                const val = Number(e.target.value);
                if (val >= minCustomInput && val <= maxCustomInput) {
                  setCustomInput(val);
                  setCustomInputError(false);
                } else {
                  setCustomInputError(true);
                }
              }}
              inputProps={{
                min: minCustomInput,
                max: maxCustomInput,
                style: {
                  textAlign: "center",
                },
              }}
              sx={{
                mx: theme.customSpacing.lg,
                my: theme.customSpacing.md,
                width: 100,
                input: {
                  color: theme.palette.foreground.button_secondary,
                  fontSize: theme.typography.textXS.fontSize,
                  padding: theme.customSpacing.xs,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.tertiary,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.tertiary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.brand,
                },
              }}
            />
          </ToggleButtonGroup>
        </Box>
        <EngagementFilter
          isManager={isManager}
          teamUsers={teamUsers}
          filter={filter}
          handleChange={handleChange}
        />
      </Box>
      <EngagementChart dataPoints={dataPoints} />
      <Box
        sx={{
          display: "flex",
          mb: 5,
          gap: theme.customSpacing["6xl"],
          [theme.breakpoints.down("md")]: {
            flexDirection: "column",
            gap: "20px",
          },
        }}
      >
        {mostActive && (
          <EngagementAchievementBox
            title="Most Active Day"
            icon={<StackedLineChart sx={{ fontSize: "14px" }} />}
            date={mostActive.date}
            count={mostActive.numberOfSims}
          />
        )}

        {leastActive && (
          <EngagementAchievementBox
            title="Least Active Day"
            icon={<NightlightOutlined sx={{ fontSize: "14px" }} />}
            date={leastActive.date}
            count={leastActive.numberOfSims}
          />
        )}
        {/* NEW User Stats */}
        {mostActiveUser && (
          <EngagementAchievementBox
            title="Most Active User"
            // You might want a User icon here
            icon={
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_1_22048)">
                  <path
                    d="M7.99998 9.99998C5.79084 9.99998 3.99998 8.20912 3.99998 5.99998V2.29628C3.99998 2.02038 3.99998 1.88243 4.04018 1.77197C4.10758 1.58679 4.25346 1.44092 4.43863 1.37352C4.54909 1.33331 4.68704 1.33331 4.96294 1.33331H11.037C11.3129 1.33331 11.4509 1.33331 11.5613 1.37352C11.7465 1.44092 11.8924 1.58679 11.9598 1.77197C12 1.88243 12 2.02038 12 2.29628V5.99998C12 8.20912 10.2091 9.99998 7.99998 9.99998ZM7.99998 9.99998V12M12 2.66665H13.6666C13.9773 2.66665 14.1326 2.66665 14.2551 2.71739C14.4185 2.78506 14.5482 2.91484 14.6159 3.07819C14.6666 3.2007 14.6666 3.35602 14.6666 3.66665V3.99998C14.6666 4.61996 14.6666 4.92995 14.5985 5.18428C14.4136 5.87447 13.8745 6.41356 13.1843 6.5985C12.93 6.66665 12.62 6.66665 12 6.66665M3.99998 2.66665H2.33331C2.02269 2.66665 1.86737 2.66665 1.74486 2.71739C1.58151 2.78506 1.45172 2.91484 1.38406 3.07819C1.33331 3.2007 1.33331 3.35602 1.33331 3.66665V3.99998C1.33331 4.61996 1.33331 4.92995 1.40146 5.18428C1.5864 5.87447 2.12549 6.41356 2.81567 6.5985C3.07001 6.66665 3.38 6.66665 3.99998 6.66665M4.96294 14.6666H11.037C11.2007 14.6666 11.3333 14.534 11.3333 14.3703C11.3333 13.0612 10.2721 12 8.96294 12H7.03702C5.7279 12 4.66665 13.0612 4.66665 14.3703C4.66665 14.534 4.7993 14.6666 4.96294 14.6666Z"
                    stroke="#FEFEFE"
                    stroke-width="1.39167"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1_22048">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            }
            user={getUserName(mostActiveUser.userId)} // Display Name
            count={mostActiveUser.totalSims} // Display Total Count
          />
        )}

        {leastActiveUser && (
          <EngagementAchievementBox
            title="Least Active User"
            icon={
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_12_22768)">
                  <path
                    d="M11 11.3333H14.3333L11 14.6666H14.3333M14.6337 8.66665C14.6555 8.44738 14.6666 8.22498 14.6666 7.99998C14.6666 4.31808 11.6819 1.33331 7.99998 1.33331C4.31808 1.33331 1.33331 4.31808 1.33331 7.99998C1.33331 11.6819 4.31808 14.6666 7.99998 14.6666C8.11175 14.6666 8.22289 14.6639 8.33331 14.6585C8.44517 14.6529 8.5563 14.6447 8.66665 14.6337M7.99998 3.99998V7.99998L10.4922 9.2461"
                    stroke="#FEFEFE"
                    stroke-width="1.39167"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_12_22768">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            }
            user={getUserName(leastActiveUser.userId)}
            count={leastActiveUser.totalSims}
          />
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <DefaultButton
          onClick={() => handleExport()}
          endIcon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.5 12.5V13.5C17.5 14.9001 17.5 15.6002 17.2275 16.135C16.9878 16.6054 16.6054 16.9878 16.135 17.2275C15.6002 17.5 14.9001 17.5 13.5 17.5H6.5C5.09987 17.5 4.3998 17.5 3.86502 17.2275C3.39462 16.9878 3.01217 16.6054 2.77248 16.135C2.5 15.6002 2.5 14.9001 2.5 13.5V12.5M14.1667 8.33333L10 12.5M10 12.5L5.83333 8.33333M10 12.5V2.5"
                stroke="#030039"
                stroke-width="1.66667"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          }
        >
          Download CSV
        </DefaultButton>
      </Box>
    </Card>
  );
};

export default TeamEngagementPanel;
